import React, { useMemo, useState } from "react"
import Button from "../../../ui/Button"
import { CubeIcon } from "@heroicons/react/24/outline"
import IAddOn, { APPLICABLE_AT } from "../../../../interfaces/IAddOn"
import { CampingStyleIcon } from "../../../ui/CampingStyleSelector"
import HoverTip from "../../../ui/HoverTip"
import ProductModalContent from "./ProductModalContent"
import ProcessingState from "../../../ui/Admin/ProcessingState"
import formatCampingStyleName from "../../../../utils/formatCampingStyleName"
import InputFieldWithLabel from "../../Inputs/InputFieldWithLabel"
import { formatMoneyFromCents } from "~/utils/formatMoney"
import { useProducts } from "~/api2/hooks/useProducts"
import { useCamp } from "~/api2/hooks/useCamp"
import { useModal } from "~/contexts/ModalProvider"
import { useCampingStyles } from "~/api2/hooks/useCampingStyles"
import classNames from "~/utils/classNames"
import parseDateTime from "~/utils/parseDateTime"
import { getInventoryLabelAndColor } from './inventoryLabel'

export const productTypes = [
  {
    value: "normal",
    label: "Standard",
    description: "Standard product that you can add any number of times",
  },
  { value: "boolean", label: "Single", description: "Can only be applied once (ie - Lock fee)" },
  {
    value: "always-applied",
    label: "Always applied (short-term)",
    description: "Always applied to a short-term reservation for a given site type (ie - Cleaning fee)",
  },
  {
    value: "always-applied-recurring",
    label: "Always applied (long-term)",
    description: "Always applied to a long-term reservation (ie - Meter reading fee)",
  },
]

const mapType = (product) => {
  switch (product) {
    case "normal":
      return "Standard"
    case "boolean":
      return "Single"
    case "always-applied":
      return "Always applied (short-term)"
    case "always-applied-recurring":
      return "Always applied (long-term)"
    default:
      break
  }
}

const Products: React.FC = () => {
  const { openModal, closeModal } = useModal()
  const {
    index: { data: camp, isLoading: isLoadingCamp },
  } = useCamp()
  const {
    index: { data: campingStyles, isLoading: isLoadingCampingStyles },
  } = useCampingStyles()
  const { data, isLoading: isLoadingProducts } = useProducts()
  const products = data?.["add_ons"]

  const [searchInput, setSearchInput] = useState<string>("")

  const handleProductClick = (selectedProduct?: IAddOn) => {
    openModal("generic", {
      children: <ProductModalContent selectedProduct={selectedProduct} />,
      onClose: () => {
        closeModal()
      },
    })
  }

  const renderProducts = () => {
    let filteredProducts = products?.filter((product) => product.kind !== "hidden")

    if (searchInput.length) {
      return filteredProducts?.filter(
        (product) =>
          product.kind?.toLowerCase().includes(searchInput?.toLowerCase()) ||
          product.description?.toLowerCase().includes(searchInput?.toLowerCase()) ||
          product.label?.toLowerCase().includes(searchInput?.toLowerCase()) ||
          product.slug?.toLowerCase().includes(searchInput?.toLowerCase()),
      )
    }

    return filteredProducts
  }

  const productCampingStyles = (product: IAddOn) =>
    campingStyles?.filter((style) => style?.id === product.camping_style_id)

  const gridTemplateColumns = "minmax(330px, 35%) 15% 15% 15% 10% 20%"

  const memoizedProducts = useMemo(() => renderProducts(), [renderProducts])

  const isLoading = isLoadingCamp || isLoadingCampingStyles || isLoadingProducts

  return (
    <div className="relative">
      {isLoading ? (
        <ProcessingState loadingMessages={[]} successMessage={""} />
      ) : (
        <>
          <div className="flex justify-between wrap gap-3 items-center flex-row">
            <div className="my-6 space-y-2">
              <div className="text-xl font-semibold text-gray-900">Products</div>
              <div className="font-medium text-gray-500">
                Products can be used internally and/or shown to campers during booking.
              </div>
            </div>
            <Button
              className="whitespace-nowrap w-32 xs:w-40"
              variant="blue"
              onClick={(e) => {
                e.preventDefault()
                handleProductClick()
              }}
            >
              Add product
            </Button>
          </div>
          <InputFieldWithLabel
            className="py-1"
            placeholder="Search product..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {memoizedProducts?.length ? (
            <>
              <div className="overflow-auto">
                <div className="grid" style={{ gridTemplateColumns, minWidth: 840 }}>
                  <div className="font-semibold text-md text-gray-900 py-5 whitespace-nowrap px-2">Name</div>
                  <div className="font-semibold text-md text-gray-900 py-5 whitespace-nowrap px-2">Unit price</div>
                  <div className="font-semibold text-md text-gray-900 py-5 whitespace-nowrap px-2">Type</div>
                  <div className="font-semibold text-md text-gray-900 py-5 whitespace-nowrap px-2">Camping style</div>
                  <div className="font-semibold text-md text-gray-900 py-5 whitespace-nowrap px-2">Bookable</div>
                  <div className="font-semibold text-md text-gray-900 py-5 whitespace-nowrap px-2">Range</div>
                </div>
                {memoizedProducts.map((product) => (
                  <div
                    className="grid hover:bg-gray-200 cursor-pointer"
                    style={{
                      gridTemplateColumns,
                      transition: "all .3s",
                      minWidth: 840,
                    }}
                    onClick={() => {
                      handleProductClick(product)
                    }}
                  >
                    <div className="font-medium text-md text-gray-900 flex gap-4 border-t py-5 px-2" key={product?.id}>
                      <div
                        style={{ minWidth: 38.5 }}
                        className="w-11 h-11 bg-gray-100 rounded-full flex items-center justify-center text-gray-475"
                      >
                        <CubeIcon style={{ width: 18, height: 18 }} />
                      </div>
                      <div
                        className={!product?.description ? "flex items-center" : "truncate"}
                        style={{ flex: "1 1 80%" }}
                      >
                        <div>
                          {product?.label}
                          {typeof product.inventory_quantity === 'number' && (() => {
                            const { label: invLabel, color } = getInventoryLabelAndColor(product.inventory_quantity)
                            return invLabel ? (
                              <span style={{ color, marginLeft: 8 }}>{invLabel}</span>
                            ) : null
                          })()}
                        </div>
                        {product?.description && (
                          <div className="truncate whitespace-nowrap text-gray-475">{product?.description}</div>
                        )}
                      </div>
                    </div>
                    <div className="font-medium text-md flex items-center border-t px-2 text-gray-475">
                      {formatMoneyFromCents(product?.amount_in_cents, camp?.currency)}
                    </div>
                    <div className="font-medium text-md flex items-center border-t px-2">
                      <div className="text-gray-475">{mapType(product?.kind)}</div>
                    </div>
                    {productCampingStyles(product)?.length >= 1 ? (
                      <div className="font-medium text-md flex items-center pl-4 border-t">
                        <div
                          className={`grid grid-cols-1 scrollbar-thin justify-center w-full`}
                          style={{ maxHeight: 74.5 }}
                        >
                          <div>
                            <HoverTip
                              className="w-auto"
                              tipPosition="absolute"
                              children={
                                <CampingStyleIcon
                                  campingStyle={productCampingStyles(product)[0]}
                                  style={{
                                    maxWidth: 40,
                                    maxHeight: 40,
                                    width: "auto",
                                    height: "auto",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                />
                              }
                              content={formatCampingStyleName(productCampingStyles(product)[0]?.name)}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="border-t grid grid-cols-1"></div>
                    )}
                    <div className="font-medium whitespace-nowrap text-md flex items-center justify-between gap-3 border-t px-2 text-gray-475">
                      {product?.is_bookable ? (
                        <div className="flex items-center gap-3">
                          <div className="rounded-full w-1.5 h-1.5 bg-green-400"></div>
                          <div className="text-green-400">Yes</div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <div className="rounded-full w-1.5 h-1.5 bg-gray-400"></div>
                          <div className="text-gray-400">No</div>
                        </div>
                      )}
                    </div>
                    <div
                      className={classNames(
                        "font-medium text-md flex border-t px-2 text-gray-475 ",
                        (product.applicable_at === APPLICABLE_AT.IN_DATE_RANGE && "flex-row items-center") ||
                          "flex-col justify-center items-start",
                      )}
                    >
                      {product?.valid_from && (
                        <span>{`${(product.applicable_at === APPLICABLE_AT.FROM_DATE && "Since ") || ""} ${parseDateTime(product?.valid_from, camp?.timezone)?.toFormat(camp.date_format.replace(/DD/g, "dd").replace(/YYYY/g, "yyyy"))}`}</span>
                      )}
                      {product.applicable_at === APPLICABLE_AT.IN_DATE_RANGE && <span>&nbsp;-&nbsp;</span>}
                      {product?.valid_until && (
                        <span>{`${(product.applicable_at === APPLICABLE_AT.UNTIL_DATE && "Until ") || ""}  ${parseDateTime(product?.valid_until, camp?.timezone)?.toFormat(camp.date_format.replace(/DD/g, "dd").replace(/YYYY/g, "yyyy"))}`}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="font-medium text-gray-500 text-2xl text-center mt-5">No products available...</div>
          )}
        </>
      )}
    </div>
  )
}

export default Products
